import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { GENDER_VALUES } from '@/types/gender';
import { type DatePrecision } from '@/utils/dateFormatting';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';

import './person-edit-form.css';

export interface PersonFormValue {
  givenName: string;
  middleName: string;
  surname: string;
  suffix: string;
  preferredName: string;
  gender: string;
  birthDate: string;
  birthDatePrecision: DatePrecision;
  birthPlace: string;
  deathDate: string;
  deathDatePrecision: DatePrecision;
  deathPlace: string;
  /** When true, death date/place fields are shown and included in submit. */
  isDeceased?: boolean;
}

export interface RelationshipToParentOption {
  value: string;
  label: string;
}

export interface PersonFormFieldsProps {
  value: PersonFormValue;
  onChange: (field: string, value: string) => void;
  onPrecisionChange: (precision: DatePrecision) => void;
  onBirthDateChange: (rawValue: string) => void;
  onDeathPrecisionChange?: (precision: DatePrecision) => void;
  onDeathDateChange?: (rawValue: string) => void;
  /** When provided, shows Deceased checkbox and gates death fields. */
  onDeceasedChange?: (deceased: boolean) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  /** When set, shows the "Relationship to parent" field (add-child flow only) */
  relationshipToParent?: {
    value: string;
    options: RelationshipToParentOption[];
    onChange: (value: string) => void;
  };
}

/**
 * Presentational form fields for adding or editing a person: basic info (names, gender),
 * optional relationship-to-parent, birth info, and death info. No form wrapper, header, or
 * submit/cancel — the parent composes these. Used by AddFamilyMemberScreen (add self / add child/spouse).
 */
const PersonFormFields: React.FC<PersonFormFieldsProps> = ({
  value,
  onChange,
  onPrecisionChange,
  onBirthDateChange,
  onDeathPrecisionChange,
  onDeathDateChange,
  onDeceasedChange,
  errors,
  disabled = false,
  relationshipToParent
}) => {
  const { t } = useTranslation('forms');
  const isDeceased = value.isDeceased ?? false;
  return (
    <>
      <div className="person-edit-form__section">
        <h3 className="person-edit-form__section-title">{t('person.basicInformation')}</h3>
        {relationshipToParent && (
          <div className="person-edit-form__field">
            <label htmlFor="parentChildRelationshipType" className="person-edit-form__label">
              {t('person.relationshipType')}
            </label>
            <Select.Root
              value={relationshipToParent.value}
              onValueChange={relationshipToParent.onChange}
              disabled={disabled}
            >
              <Select.Trigger
                id="parentChildRelationshipType"
                className="form-select-trigger"
                aria-label={t('person.relationshipType')}
              >
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {relationshipToParent.options.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value} className="form-select-item">
                      <Select.ItemText>{opt.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
        )}
        <div className="person-edit-form__field-group">
          <div className="person-edit-form__field">
            <label htmlFor="givenName" className="person-edit-form__label">
              {t('person.givenNameRequired')}
            </label>
            <input
              id="givenName"
              type="text"
              value={value.givenName}
              onChange={(e) => onChange('givenName', e.target.value)}
              required
              disabled={disabled}
              className={`person-edit-form__input ${errors.givenName ? 'person-edit-form__input--error' : ''}`}
            />
            {errors.givenName && <span className="person-edit-form__error-message">{errors.givenName}</span>}
          </div>
          <div className="person-edit-form__field">
            <label htmlFor="middleName" className="person-edit-form__label">
              {t('person.middleName')}
            </label>
            <input
              id="middleName"
              type="text"
              value={value.middleName}
              onChange={(e) => onChange('middleName', e.target.value)}
              disabled={disabled}
              className="person-edit-form__input"
            />
          </div>
          <div className="person-edit-form__field">
            <label htmlFor="surname" className="person-edit-form__label">
              {t('person.surname')}
            </label>
            <input
              id="surname"
              type="text"
              value={value.surname}
              onChange={(e) => onChange('surname', e.target.value)}
              disabled={disabled}
              className="person-edit-form__input"
            />
          </div>
        </div>
        <div className="person-edit-form__field-group">
          <div className="person-edit-form__field">
            <label htmlFor="suffix" className="person-edit-form__label">
              {t('person.suffix')}
            </label>
            <input
              id="suffix"
              type="text"
              value={value.suffix}
              onChange={(e) => onChange('suffix', e.target.value)}
              disabled={disabled}
              className="person-edit-form__input"
              placeholder={t('person.suffixPlaceholder')}
            />
          </div>
          <div className="person-edit-form__field">
            <label htmlFor="preferredName" className="person-edit-form__label">
              {t('person.preferredName')}
            </label>
            <input
              id="preferredName"
              type="text"
              value={value.preferredName}
              onChange={(e) => onChange('preferredName', e.target.value)}
              disabled={disabled}
              className="person-edit-form__input"
            />
          </div>
          <div className="person-edit-form__field">
            <label htmlFor="gender" className="person-edit-form__label">
              {t('person.gender')}
            </label>
            <Select.Root value={value.gender} onValueChange={(v) => onChange('gender', v)} disabled={disabled}>
              <Select.Trigger id="gender" className="form-select-trigger" aria-label={t('person.gender')}>
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {GENDER_VALUES.map((gender) => (
                    <Select.Item key={gender} value={gender} className="form-select-item">
                      <Select.ItemText>{t(`gender.${gender}`)}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
        </div>
      </div>

      <div className="person-edit-form__section">
        <h3 className="person-edit-form__section-title">{t('person.birthInformation')}</h3>
        <div className="person-edit-form__field-group">
          <div className="person-edit-form__field">
            <DateFieldWithPrecision
              id="birthDate"
              label={t('person.birthDate')}
              value={value.birthDate ?? ''}
              precision={value.birthDatePrecision}
              onValueChange={onBirthDateChange}
              onPrecisionChange={onPrecisionChange}
              onErrorClear={() => {}}
              disabled={disabled}
              error={errors.birthDate}
              allowClearViaInput
              layout="inline"
            />
          </div>
          <div className="person-edit-form__field">
            <label htmlFor="birthPlace" className="person-edit-form__label">
              {t('person.birthPlace')}
            </label>
            <input
              id="birthPlace"
              type="text"
              value={value.birthPlace}
              onChange={(e) => onChange('birthPlace', e.target.value)}
              disabled={disabled}
              className="person-edit-form__input"
            />
          </div>
        </div>
      </div>

      <div className="person-edit-form__section">
        <h3 className="person-edit-form__section-title">{t('person.deathInformation')}</h3>
        {onDeceasedChange != null ? (
          <>
            <div className="person-edit-form__field-group">
              <div className="person-edit-form__field person-edit-form__field--full">
                <label className="person-edit-form__label person-edit-form__checkbox-label">
                  <input
                    type="checkbox"
                    checked={isDeceased}
                    onChange={(e) => onDeceasedChange(e.target.checked)}
                    disabled={disabled}
                    className="person-edit-form__checkbox"
                    aria-describedby="add-deceased-desc"
                  />
                  <span>{t('person.deceased')}</span>
                </label>
                <span id="add-deceased-desc" className="person-edit-form__hint">
                  {t('person.deceasedHint')}
                </span>
              </div>
            </div>
            {isDeceased && (
              <div className="person-edit-form__field-group">
                <div className="person-edit-form__field">
                  <DateFieldWithPrecision
                    id="deathDate"
                    label={t('person.deathDate')}
                    value={value.deathDate ?? ''}
                    precision={value.deathDatePrecision}
                    onValueChange={(v) => onDeathDateChange?.(v)}
                    onPrecisionChange={(p) => onDeathPrecisionChange?.(p)}
                    onErrorClear={() => {}}
                    disabled={disabled}
                    error={errors.deathDate}
                    allowClearViaInput
                    layout="inline"
                  />
                  <span className="person-edit-form__hint">{t('person.dateOptionalHint')}</span>
                </div>
                <div className="person-edit-form__field">
                  <label htmlFor="deathPlace" className="person-edit-form__label">
                    {t('person.deathPlace')}
                  </label>
                  <input
                    id="deathPlace"
                    type="text"
                    value={value.deathPlace}
                    onChange={(e) => onChange('deathPlace', e.target.value)}
                    disabled={disabled}
                    className="person-edit-form__input"
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="person-edit-form__field-group">
            <div className="person-edit-form__field">
              <DateFieldWithPrecision
                id="deathDate"
                label={t('person.deathDate')}
                value={value.deathDate ?? ''}
                precision={value.deathDatePrecision}
                onValueChange={(v) => onDeathDateChange?.(v)}
                onPrecisionChange={(p) => onDeathPrecisionChange?.(p)}
                onErrorClear={() => {}}
                disabled={disabled}
                error={errors.deathDate}
                allowClearViaInput
                layout="inline"
              />
            </div>
            <div className="person-edit-form__field">
              <label htmlFor="deathPlace" className="person-edit-form__label">
                {t('person.deathPlace')}
              </label>
              <input
                id="deathPlace"
                type="text"
                value={value.deathPlace}
                onChange={(e) => onChange('deathPlace', e.target.value)}
                disabled={disabled}
                className="person-edit-form__input"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PersonFormFields;
